import { useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";

import { toCount } from "common/utils/formatting";
import { toggleValue } from "common/utils/sets";
import { PaymentMethod } from "common/types";
import CatalogImage from "common/components/CatalogImage";
import { deepCombineErrors } from "common/utils/errors";
import { titleCased } from "common/utils/strings";

import { ListSubscription } from "client/subscriptions/types";
import { useUpdateBulkSubscriptionsMutation } from "client/subscriptions/hooks";
import Checkbox from "client/common/components/inputs/Checkbox";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useCurrentDrawer } from "client/common/hooks/stackables";

import styles from "../../styles/SubscriptionBulkUpdatePaymentMethod.module.less";

export type SubscriptionBulkUpdatePaymentMethodDrawerProps = {
  subscriptions: ListSubscription[];
  paymentMethod: PaymentMethod;
  onSuccess: () => void;
};

const SubscriptionBulkUpdatePaymentMethodDrawer = ({
  subscriptions,
  paymentMethod,
  onSuccess,
}: SubscriptionBulkUpdatePaymentMethodDrawerProps) => {
  const { close } = useCurrentDrawer();
  const stripe = useStripe();

  const handleClose = () => {
    onSuccess();
    close();
  };

  const {
    mutate: bulkUpdate,
    isLoading,
    error,
  } = useUpdateBulkSubscriptionsMutation({
    onSuccess: handleClose,
    onError: (e, data) => {
      if (!stripe || !e.data?.stripe_action_client_secret) {
        return;
      }
      stripe?.handleCardAction(e.data?.stripe_action_client_secret).then((result) => {
        if (result.paymentIntent) {
          bulkUpdate(data);
        }
      });
    },
  });

  const [appliedSubscriptions, setAppliedSubscriptions] = useState<Set<number>>(
    new Set(subscriptions.map((subscription) => subscription.id)),
  );

  const onSubscriptionToggled = (subscriptionId: number) => {
    setAppliedSubscriptions(toggleValue(appliedSubscriptions, subscriptionId));
  };
  const onSubmit = () => {
    bulkUpdate({
      payment_method_id: paymentMethod?.id,
      subscription_ids: [...appliedSubscriptions],
    });
  };

  const modalLayoutHeader = (
    <div className={styles.nameContainer}>
      You have Autoships with another payment method on file. Select the Autoships you would like to
      update to the new payment method.
    </div>
  );

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Update your Autoships"
        description={modalLayoutHeader}
        onClose={close}
      />
      <DrawerLayout.Form
        canSubmit={true}
        onSubmit={onSubmit}
        submitText={`Update ${toCount(appliedSubscriptions.size, "Autoship")}`}
        isLoading={isLoading}
        error={error}
        fieldErrors={[deepCombineErrors(error?.errors)]}
      >
        {subscriptions.map((subscription) => (
          <DrawerLayout.Form.Group key={subscription.id}>
            <div className={styles.option}>
              <Checkbox
                value={appliedSubscriptions.has(subscription.id)}
                onChange={() => onSubscriptionToggled(subscription.id)}
              />
              <div className={styles.item}>
                <CatalogImage
                  image={subscription.item.image}
                  size="thumbnail"
                  alt={subscription.item.full_name}
                  className={styles.image}
                />
                <div className={styles.nameContainer}>
                  <div className={styles.name}>
                    {subscription.item.product.product_line.name} {subscription.item.product.name}{" "}
                    for <b>{subscription.patient.name_display}</b>
                  </div>
                  {paymentMethod && subscription.payment_method && (
                    <div className={styles.name}>
                      Currently charged to:
                      <div className={styles.name}>
                        <b>
                          {subscription.payment_method.is_klarna
                            ? subscription.payment_method.alt_ui_string
                            : `${titleCased(subscription.payment_method.brand)} *${
                                subscription.payment_method.last_four
                              }, exp. ${subscription.payment_method.expiration_month}/${
                                subscription.payment_method.expiration_year
                              }`}
                        </b>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DrawerLayout.Form.Group>
        ))}
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default SubscriptionBulkUpdatePaymentMethodDrawer;
